import React from "react";
import { Controller, useForm } from "react-hook-form";
import { AiOutlineInfoCircle } from "react-icons/ai";
import ReactSelect from "react-select";
import PricingIndicator from "./PricingIndicator";
import Countries from "./data/country-rates.json";
import inrRates from "./data/inr-rates.json";
import usdRates from "./data/usd-rates.json";
import ToggleSwitch from "./ToggleSwitch";

export interface CalculatorFormProps {
    countryName: CountryOptionsProps;
    marketingConvoCount: number;
    utilityConvoCount: number;
    authenticationConvoCount: number;
    serviceConvoCount: number;
    planType: "all" | "pro";
    activeWindow: boolean;
}

interface CountriesNameProps {
    currency: string;
    countryCode: number;
    countryName: string;
    countryISO: string;
    priceID: number;
}

export interface CountryOptionsProps {
    label: string;
    value: number;
    currencyType: "INR" | "USD";
}

// Default values to be displayed
const defaultRates = {
    USD: {
        authentication: 30.4,
        utility: 33.8,
        marketing: 60.4,
        service: 14.5,
        authentication_international: 0,
    },
    INR: {
        authentication: 2229.2,
        utility: 2476.1,
        marketing: 4424.8,
        service: 1061.5,
        authentication_international: 0,
    },
};

//Conversation Charge
export const getWhatsappPricingV2 = (countryName: string, currency: "INR" | "USD", category: "marketing" | "utility" | "authentication" | "service"): number => {
    const defaultAmount: number = defaultRates[currency][category];

    const { priceID } = Countries.find((p) => p.countryName === countryName && p.currency === currency) ?? {};
    if (priceID == null) return defaultAmount;

    const rateCard = currency === "USD" ? usdRates : inrRates;
    const amount = rateCard.find((i) => i.priceID === priceID)?.[category];
    if (amount == null) return defaultAmount;

    return amount;
};

// List of countries
const uniqueCountryList = () => {
    const filteredCountryList: CountriesNameProps[] = [];
    const encounteredCountries = new Set();
    const input = Countries.filter((item) => item.currency === "USD");
    input.forEach((filteredItem) => {
        if (!encounteredCountries.has(filteredItem.countryName)) {
            encounteredCountries.add(filteredItem.countryName);
            filteredCountryList.push(filteredItem);
        }
    });
    return filteredCountryList;
};

const countryOptions: CountryOptionsProps[] = uniqueCountryList().map((country) => ({
    label: country.countryName,
    value: country.countryCode,
    currencyType: country.currency as CountryOptionsProps["currencyType"],
}));

const tooltipText = [
    "Messages related to promotions, updates, offers or invitations that prompt customers to respond and engage",
    "Messages related to Post-purchase notifications, recurring billing statements",
    "Enables you authenticate users with one-time passcodes, potentially at multiple steps in the login process",
    "User-initiated messages, primarily customer service requests",
];

const percentageCalculation = (baseValue: number, percentage: number) => {
    return baseValue * (percentage / 100);
};

const PricingCalculator: React.FC = () => {
    const isIndia = new Date().toString().includes("India Standard Time") || new Date().toString().includes("IST");

    const { register, control, setValue, watch } = useForm<CalculatorFormProps>({
        defaultValues: {
            countryName: isIndia ? { label: "India", value: 91, currencyType: "INR" } : undefined,
            marketingConvoCount: 2200,
            utilityConvoCount: 2200,
            authenticationConvoCount: 2200,
            serviceConvoCount: 2200,
            activeWindow: false,
            planType: "all",
        },
    });

    const country = watch("countryName") ?? {};

    const userSelectedPlan = watch("planType");

    const percentageBasedOnPlan = userSelectedPlan === "all" ? 15 : 10;

    const isINR = country.value === 91;

    // Calculation for individual conversation charges

    const baseMarketingamount = getWhatsappPricingV2(country.label, isINR ? "INR" : "USD", "marketing") / 1000;
    const baseUtilityamount = getWhatsappPricingV2(country.label, isINR ? "INR" : "USD", "utility") / 1000;
    const baseAuthenticationamount = getWhatsappPricingV2(country.label, isINR ? "INR" : "USD", "authentication") / 1000;

    const marketingCostPer24Hours = baseMarketingamount + percentageCalculation(baseMarketingamount, percentageBasedOnPlan);

    const utilityCostPer24Hours = baseUtilityamount + percentageCalculation(baseUtilityamount, percentageBasedOnPlan);

    const authenticationCostPer24Hours = baseAuthenticationamount + percentageCalculation(baseAuthenticationamount, percentageBasedOnPlan);

    const currencySymbol = isINR ? "₹" : "$";

    const formatCurrency = (value: number) => {
        const locale = isINR ? "en-IN" : "en-US";
        return new Intl.NumberFormat(locale, { style: "currency", currency: isINR ? "INR" : "USD" }).format(value);
    };

    const marketingConvoCharges = watch("marketingConvoCount") * marketingCostPer24Hours;
    const marketingConvoChargesFormatted = formatCurrency(parseFloat(marketingConvoCharges.toFixed(2)));

    const utilityConvoCharges = watch("utilityConvoCount") * utilityCostPer24Hours;
    const utilityConvoChargesFormatted = formatCurrency(parseFloat(utilityConvoCharges.toFixed(2)));

    const authenticationConvoCharges = watch("authenticationConvoCount") * authenticationCostPer24Hours;
    const authenticationConvoChargesFormatted = formatCurrency(parseFloat(authenticationConvoCharges.toFixed(2)));

    const active24Window = watch("activeWindow");

    const totalEstimate = marketingConvoCharges + utilityConvoCharges + authenticationConvoCharges;
    const totalEstimateFormatted = formatCurrency(totalEstimate);

    return (
        <div className="lg:min-w-xl lg:max-w-xl md:min-w-lg md:max-w-lg w-fit p-8 flex flex-col items-center gap-6">
            <form className="w-full">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 w-full">
                        <CalculatorFormLabel htmlFor="countryName">Receiver&apos;s Country</CalculatorFormLabel>
                        <Controller
                            name="countryName"
                            control={control}
                            render={({ field }) => <ReactSelect {...field} options={countryOptions} components={{ IndicatorSeparator: null }} />}
                        />
                    </div>

                    <div className="flex items-center gap-5">
                        <div className="flex items-center gap-3">
                            <label htmlFor="all">All</label>
                            <input type="radio" id="all" value={"all"} {...register("planType")} />
                        </div>
                        <div className="flex items-center gap-3">
                            <label htmlFor="pro">Pro</label>
                            <input type="radio" id="pro" value={"pro"} {...register("planType")} />
                        </div>
                    </div>

                    <div className="pb-4 flex flex-col gap-4 mt-1">
                        <div className="flex justify-between">
                            <div className="flex flex-col">
                                <CalculatorFormLabel htmlFor="marketingConvo">
                                    <div className="flex items-center gap-1">
                                        Marketing Message
                                        <span className="relative" style={{ width: "15px", height: "15px" }}>
                                            <AiOutlineInfoCircle className={"pricing-tooltip-icon"} style={{ color: "#4A5568" }} size="15px" />
                                            <div style={{ width: "300px", transform: "translate(-46%)", left: "0px" }} className={"pricing-tooltip-content"}>
                                                {tooltipText[0]}
                                            </div>
                                        </span>{" "}
                                    </div>
                                </CalculatorFormLabel>
                                <p className="text-base font-normal text-gray-500">
                                    {currencySymbol}
                                    {marketingCostPer24Hours.toFixed(4)}
                                </p>
                            </div>
                            <p className="text-base font-bold text-blue-500 flex justify-center items-center">{marketingConvoChargesFormatted}</p>
                        </div>
                        <div>
                            <PricingIndicator
                                register={register}
                                watch={watch}
                                name="marketingConvoCount"
                                handleClick={(value: number) => setValue("marketingConvoCount", value)}
                            />
                        </div>
                    </div>
                    <div className="pb-4 flex flex-col gap-4 mt-1">
                        <div className="flex justify-between">
                            <div className="flex flex-col">
                                <CalculatorFormLabel htmlFor="utilityConvo">
                                    <div className="flex items-center gap-1">
                                        Utility Message
                                        <span className="relative" style={{ width: "15px", height: "15px" }}>
                                            <AiOutlineInfoCircle className={"pricing-tooltip-icon"} style={{ color: "#4A5568" }} size="15px" />
                                            <div style={{ width: "300px", transform: "translate(-46%)", left: "0px" }} className={"pricing-tooltip-content"}>
                                                {tooltipText[1]}
                                            </div>
                                        </span>{" "}
                                    </div>
                                </CalculatorFormLabel>
                                {!active24Window ? (
                                    <p className="text-base font-normal text-gray-500">
                                        {currencySymbol}
                                        {utilityCostPer24Hours.toFixed(4)}/24 hours
                                    </p>
                                ) : (
                                    <p className="text-base font-normal text-gray-500"> {currencySymbol}0</p>
                                )}
                            </div>
                            <p className="text-base font-bold text-blue-500 flex justify-center items-center">
                                {active24Window ? `${currencySymbol}0` : utilityConvoChargesFormatted}
                            </p>
                        </div>
                        <div>
                            <PricingIndicator register={register} watch={watch} name="utilityConvoCount" handleClick={(value: number) => setValue("utilityConvoCount", value)} />
                        </div>
                    </div>
                    <div className="pb-4 flex flex-col gap-4 mt-1">
                        <div className="flex justify-between">
                            <div className="flex flex-col">
                                <CalculatorFormLabel htmlFor="authenticationConvo">
                                    <div className="flex items-center gap-1">
                                        Authentication Message
                                        <span className="relative" style={{ width: "15px", height: "15px" }}>
                                            <AiOutlineInfoCircle className={"pricing-tooltip-icon"} style={{ color: "#4A5568" }} size="15px" />
                                            <div style={{ width: "300px", transform: "translate(-46%)", left: "0px" }} className={"pricing-tooltip-content"}>
                                                {tooltipText[2]}
                                            </div>
                                        </span>{" "}
                                    </div>
                                </CalculatorFormLabel>
                                {!active24Window ? (
                                    <p className="text-base font-normal text-gray-500">
                                        {currencySymbol}
                                        {authenticationCostPer24Hours.toFixed(4)}/24 hours
                                    </p>
                                ) : (
                                    <p className="text-base font-normal text-gray-500"> {currencySymbol}0</p>
                                )}
                            </div>
                            <p className="text-base font-bold text-blue-500 flex justify-center items-center">
                                {active24Window ? `${currencySymbol}0` : authenticationConvoChargesFormatted}
                            </p>
                        </div>
                        <div>
                            <PricingIndicator
                                register={register}
                                watch={watch}
                                name="authenticationConvoCount"
                                handleClick={(value: number) => setValue("authenticationConvoCount", value)}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-4 mt-2">
                        <div className="flex justify-between">
                            <div className="flex flex-col">
                                <CalculatorFormLabel htmlFor="authenticationConvo">
                                    <div className="flex items-center gap-1">Active 24h window</div>
                                </CalculatorFormLabel>
                            </div>
                            <Controller name="activeWindow" control={control} render={({ field }) => <ToggleSwitch checked={field.value} onChange={field.onChange} />} />
                            {/* <p className="text-base font-bold text-blue-500 flex justify-center items-center">{authenticationConvoChargesFormatted}</p> */}
                        </div>
                    </div>
                </div>
            </form>
            <div className="flex flex-col gap-2.5 items-center w-full">
                <div className="px-5 py-3 flex justify-between items-center rounded-xl w-full" style={{ backgroundColor: "#0083FF", color: "white" }}>
                    <span className="text-base font-medium text-white">Estimate</span>
                    <span className="text-2xl font-bold text-white flex gap-2">
                        {active24Window ? marketingConvoChargesFormatted : totalEstimateFormatted}
                        <div style={{ width: "1px" }} className="h-25 bg-white"></div>
                        <span className="text-base font-normal text-white flex items-center">{isINR ? "INR" : "USD"}</span>
                    </span>
                </div>
                {/* <div className="p-3 border border-solid border-green-200 bg-green-50 rounded-lg flex items-center gap-2">
                    <div className="lg:block md:block hidden w-24 h-16">
                        <Lottie animationData={giftJson} loop />
                    </div>
                    <div className="flex flex-col lg:gap-0 md:gap-0 gap-1">
                        <h5 className="text-base font-bold text-gray-700 lg:text-left md:text-left text-center">
                            <a href="https://app.gallabox.com/signup">Get 1,000 Free Service Conversations Every Month!</a>
                        </h5>
                        <p className="text-sm font-normal text-gray-500">
                            Plus for each connected WhatsApp Business Account, get 1000 user-initiated (service) conversations free every month
                        </p>
                    </div>
                </div> */}
                <p className="text-black text-center text-md md:text-left mt-2">
                    Plus for each connected WhatsApp Business Account, get 1000 user-initiated (service) message free every month{" "}
                </p>
                <div className="flex items-start text-gray-500 font-normal text-sm text-center">
                    {/* <div className="w-5 h-5 p-0.5">
                        <BsFillInfoCircleFill size="15px" className="text-blue-300" />
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default PricingCalculator;

type CalculatorLabelProps = React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;
export const CalculatorFormLabel: React.FC<CalculatorLabelProps> = (props) => {
    const { children, ...labelProps } = props;
    return (
        <label {...labelProps} className="text-base font-bold text-gray-700">
            {children}
        </label>
    );
};
